import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { OrgStructureItem, OrgStructureNameIdEntry, AttachedPositionItem } from "@ServerTypes/orgUnits/orgUnits.schema";

/**
 * OrgStructure state: completeData holds all org units.
 * Additional dynamic keys map org type IDs to type names (string)
 * or type names to arrays of { id, name } entries.
 */
export interface OrgStructureState {
  completeData: OrgStructureItem[];
  [key: string]: OrgStructureItem[] | OrgStructureNameIdEntry[] | string | undefined;
}

const initialOrgStructureData: OrgStructureState = {
  completeData: [],
};

const orgStructureSlice = createSlice({
  name: "orgStructureData",
  initialState: initialOrgStructureData,
  reducers: {
    // Accepts OrgStructureState or a plain object (from action file) and casts it to the state type
    setOrgStructureData: (_state: OrgStructureState, action: PayloadAction<OrgStructureState>) =>
      action.payload as OrgStructureState,
    addOrgStructureData: (state: OrgStructureState, action: PayloadAction<OrgStructureItem>) => {
      state.completeData.unshift(action.payload);
    },
    updateOrgStructureData: (state: OrgStructureState, action: PayloadAction<OrgStructureItem>) => {
      state.completeData = state.completeData.map((data: OrgStructureItem) =>
        data.id === action.payload.id ? action.payload : data,
      );
    },
    removeOrgStructureData: (state: OrgStructureState, action: PayloadAction<string[]>) => {
      state.completeData = state.completeData
        .filter((data: OrgStructureItem) => !action.payload.includes(data.id))
        .map((data: OrgStructureItem) => {
          if (data.reportsTo && action.payload.includes(data.reportsTo)) {
            return {
              ...data,
              reportsTo: "",
              reportsToName: "",
            };
          }
          return data;
        });
    },
    reorderOrgStructureData: (state: OrgStructureState, action: PayloadAction<{ id: string; parentId: string }>) => {
      state.completeData = state.completeData.map((data: OrgStructureItem) =>
        data.id === action.payload.id ? { ...data, parentId: action.payload.parentId } : data,
      );
    },
  },
});

export const {
  setOrgStructureData,
  addOrgStructureData,
  updateOrgStructureData,
  removeOrgStructureData,
  reorderOrgStructureData,
} = orgStructureSlice.actions;

export const orgStructureData = orgStructureSlice.reducer;

const projectAttachedPositionSlice = createSlice({
  name: "projectAttachedPosition",
  initialState: [] as AttachedPositionItem[],
  reducers: {
    setProjectAttachedPosition: (_state: AttachedPositionItem[], action: PayloadAction<AttachedPositionItem[]>) => action.payload,
  },
});

export const { setProjectAttachedPosition } = projectAttachedPositionSlice.actions;

export const projectAttachedPosition = projectAttachedPositionSlice.reducer;
